import { AccountChangeLanguage } from '@/domain/usecases/account/account-change-language'
import { i18nTranslationHelper } from '@/i18n'
import { makeRemoteAccountChangeLanguage } from '@/main/factories/usecases/account/remote-account-change-language-factory'
import UserLanguageForm, {
  UserLanguageImperativeType
} from '@/presentation/components/ui/form/user-language-form/user-language-form'
import SpinnerBackdropApp from '@/presentation/components/ui/spinner/spinner-backdrop-app'
import { Alert, Button, Grid, useMediaQuery, useTheme } from '@mui/material'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export type UserChangeLanguageFormValues = {
  language: string
}

export type UserChangeLanguageFormProps = {
  submitOnChange: boolean
}

export default function UserChangeLanguageForm({ submitOnChange }: UserChangeLanguageFormProps) {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const remoteChangeLanguage = makeRemoteAccountChangeLanguage()
  const languageRef = useRef<UserLanguageImperativeType | null>(null)
  const mdDownMatches = useMediaQuery(theme.breakpoints.down('md'))
  const [alertFeedback, setAlertFeedback] = useState<string | undefined>()
  const [error, setError] = useState(false)

  const defaultValues = {
    language: i18nTranslationHelper.getCurrentLanguage()
  }
  const { setValue, trigger, handleSubmit, getValues, watch } =
    useForm<UserChangeLanguageFormValues>({
      mode: 'all',
      defaultValues
    })

  useEffect(() => {
    const { unsubscribe } = watch(() => {
      closeAlertFeedback()
    }, defaultValues)
    return () => unsubscribe()
  }, [watch])

  function closeAlertFeedback() {
    setAlertFeedback(undefined)
    setError(false)
  }

  const handleNewLanguageValueSubmit = ({ language }: UserChangeLanguageFormValues) => {
    setValue('language', language)
    handleSubmit(handleValidFormValues)()
  }

  const onSubmit = () => {
    trigger()
    languageRef?.current?.submit?.()
  }

  const handleValidFormValues = () => {
    refetch()
  }

  const { isFetching: isSubmitting, refetch } = useQuery({
    queryKey: [AccountChangeLanguage.Key],
    queryFn: async () => {
      setError(false)
      const { language } = getValues()
      return remoteChangeLanguage
        .change({ language })
        .then(() => {
          window.location.reload()
        })
        .catch(error => {
          queryClient.removeQueries({ queryKey: [AccountChangeLanguage.Key] })
          setError(true)
          setAlertFeedback(error.message)
        })
    },
    enabled: false,
    staleTime: 250,
    retry: false
  })

  const t = {
    components: i18nTranslationHelper.getTComponents
  }

  return (
    <Fragment>
      <SpinnerBackdropApp
        loading={isSubmitting}
        message={t.components('userChangeLanguageForm.loadingMessage')}
      />
      <Grid size={12}>
        <UserLanguageForm
          ref={languageRef}
          onSubmit={handleNewLanguageValueSubmit}
          submitOnChange={submitOnChange}
        />
      </Grid>
      <Grid size={12} textAlign="end" mt={1}>
        {alertFeedback && (
          <Alert
            className="mb-6"
            onClose={closeAlertFeedback}
            severity={error ? 'error' : 'success'}
            variant="filled"
          >
            {alertFeedback}
          </Alert>
        )}
        {!submitOnChange && (
          <Button variant="contained" onClick={onSubmit} fullWidth={mdDownMatches}>
            {t.components('userChangeLanguageForm.mainCta')}
          </Button>
        )}
      </Grid>
    </Fragment>
  )
}
